import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  withRouter,
} from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import SwiperCore, { Autoplay } from "swiper";
// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  useGetSubjectQuery,
  useUploadSubjectContentImageMutation,
  useUploadSubjectHomeImageMutation,
} from "../../store/api/subjectApi";
import i18n from "../../i18n";
import { AddEditSubjectModal } from "./";
import {
  OrganizingLayoutPage,
  LecturesLayoutPage,
  SubjectNotesPage,
  QuestionsLayoutPage,
  SubjectDocumentPage,
} from "../";
import { IMAGE_ACCEPT } from "../../constants";
import { SubjectTabMenu } from "../../components";
import { RoleEnum } from "../../enums";
import profileBg from "../../assets/images/no image-bg.png";
import { SubjectInstructionPage } from "../SubjectInstruction";
import { FlashCardV2LayoutPage } from "../FlashCardV2";
import { SubjectLiveLecturePage } from "../LiveLecture";
import { AccountState } from "../../store/slices/accountSlice";
import { toast } from "react-toastify";
import { useGetGradeQuery } from "../../store/api/gradeApi";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetEducationSystemQuery } from "../../store/api/educationSystemApi";
import { useGetCurriculumQuery } from "../../store/api/curriculumApi";
import { useGetEducationalStageQuery } from "../../store/api/educationalStageApi";
import { LocalizedProperty } from "../../interfaces";
import {
  buildSubjectsSearch,
  getCourseIdBySlug,
  readSubjectsFiltersFromSearch,
} from "../../utils/subjectsNavigation";
// @ts-ignore
import "./SubjectDetailPage.scss";

export const SubjectDetailPageComponent = (props: any) => {
  //#region hooks
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { subjectId, gradeId, course } = useParams<any>();
  const { match } = props;
  const { path, url } = match;
  const langKey = i18n.language === "ar" ? "ar" : "en";
  const localizedName = (name?: LocalizedProperty | null) =>
    name?.[langKey] || name?.en || "";

  //#endregion

  //#region RTK Query + redux state
  const { data: subject } = useGetSubjectQuery(subjectId, {
    skip: !subjectId,
  });
  const { data: grade } = useGetGradeQuery(gradeId, { skip: !gradeId });
  const { data: courses } = useGetCourseSelectListQuery();
  const hierarchyFilters = useMemo(
    () => readSubjectsFiltersFromSearch(location.search),
    [location.search],
  );
  const { data: educationSystem } = useGetEducationSystemQuery(
    hierarchyFilters.educationSystemId,
    { skip: !hierarchyFilters.educationSystemId },
  );
  const { data: curriculum } = useGetCurriculumQuery(
    hierarchyFilters.curriculumId,
    { skip: !hierarchyFilters.curriculumId },
  );
  const { data: stage } = useGetEducationalStageQuery(
    hierarchyFilters.stageId,
    { skip: !hierarchyFilters.stageId },
  );

  const [
    uploadSubjectHomeImage,
    { isLoading: homeImageUploading, error: homeImageUploadingError },
  ] = useUploadSubjectHomeImageMutation();

  const [
    uploadSubjectContentImage,
    { isLoading: contentImageUploading, isError: isErrorContentImageUploading },
  ] = useUploadSubjectContentImageMutation();

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );

  //#endregion

  //#region internal state

  const [editingSubjectId, setEditingSubjectId] = useState<string | null>(null);
  const [showAddEditSubjectModal, setShowAddEditSubjectModal] =
    useState<boolean>(false);
  //#endregion

  document.title = `${t("documentTitle", { pageName: "subject" })}`;

  //#region handlers

  SwiperCore.use([Autoplay]);

  const onEditSubject = (id: string) => {
    setEditingSubjectId(id);
    setShowAddEditSubjectModal(true);
  };

  const onChangeSubjectHomeImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/,
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      uploadSubjectHomeImage({
        file: imageFile,
        subjectId: subject?.id,
      });
    }
  };

  const onChangeSubjectContentImage = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { target } = event;
    const { files } = target;
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF|)$/,
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      uploadSubjectContentImage({ file: imageFile, subjectId: subject?.id });
    }
  };

  const resolvedCourseId =
    hierarchyFilters.courseId || getCourseIdBySlug(courses, course);

  const subjectsListLink = useMemo(() => {
    const search = buildSubjectsSearch({
      educationSystemId: hierarchyFilters.educationSystemId,
      curriculumId: hierarchyFilters.curriculumId,
      courseId: resolvedCourseId,
      stageId: hierarchyFilters.stageId,
      gradeId: hierarchyFilters.gradeId || gradeId || "",
    });
    return `/subjects${search}`;
  }, [hierarchyFilters, resolvedCourseId, gradeId]);

  const hierarchyItems = useMemo(() => {
    const courseName =
      localizedName(
        courses?.find((c) => c.id === resolvedCourseId)?.value,
      ) || t(course);
    const gradeName =
      localizedName(grade?.name) ||
      localizedName(
        stage?.grades?.find((g) => g.id === (gradeId || hierarchyFilters.gradeId))
          ?.name,
      );

    return [
      {
        key: "educationSystem",
        value: localizedName(educationSystem?.name),
        to: `/subjects${buildSubjectsSearch({
          educationSystemId: hierarchyFilters.educationSystemId,
        })}`,
      },
      {
        key: "curriculum",
        value: localizedName(curriculum?.name),
        to: `/subjects${buildSubjectsSearch({
          educationSystemId: hierarchyFilters.educationSystemId,
          curriculumId: hierarchyFilters.curriculumId,
        })}`,
      },
      {
        key: "stage",
        value: localizedName(stage?.name),
        to: `/subjects${buildSubjectsSearch({
          educationSystemId: hierarchyFilters.educationSystemId,
          curriculumId: hierarchyFilters.curriculumId,
          courseId: resolvedCourseId,
          stageId: hierarchyFilters.stageId,
        })}`,
      },
      {
        key: "course",
        value: courseName,
        to: `/subjects${buildSubjectsSearch({
          educationSystemId: hierarchyFilters.educationSystemId,
          curriculumId: hierarchyFilters.curriculumId,
          courseId: resolvedCourseId,
          stageId: hierarchyFilters.stageId,
        })}`,
      },
      {
        key: "grade",
        value: gradeName,
        to: subjectsListLink,
      },
    ].filter((item) => Boolean(item.value));
  }, [
    courses,
    course,
    curriculum?.name,
    educationSystem?.name,
    grade?.name,
    gradeId,
    hierarchyFilters,
    resolvedCourseId,
    stage,
    subjectsListLink,
    t,
    langKey,
  ]);
  //#endregion

  //#region effects

  useEffect(() => {
    if (!subjectId) {
      history.goBack();
    }
  }, [subjectId]);

  useEffect(() => {
    if (!homeImageUploading && homeImageUploadingError) {
      const errMsg =
        homeImageUploadingError && "data" in homeImageUploadingError
          ? (homeImageUploadingError.data as string)
          : undefined;
      toast.error(errMsg || t("error"), { autoClose: 3000 });
    }
  }, [homeImageUploading, homeImageUploadingError, t]);

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="subject-detail-hero profile-foreground mx-n4 mt-n4">
            <div className="profile-wid-bg">
              <img
                src={subject?.contentImage || profileBg}
                alt="Subject Image"
                className="profile-wid-img"
              />
            </div>
            <div className="subject-detail-hero-body">
              <Row className="g-3 align-items-center">
                <Col xs="auto">
                  <div className="profile-user position-relative d-inline-block">
                    <img
                      src={subject?.homeImage || profileBg}
                      className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                      alt="user-profile"
                    />
                    {profile &&
                      profile.role &&
                      profile?.role.key == RoleEnum.SUPER_ADMIN && (
                        <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                          <Input
                            id="subject_home_image"
                            type="file"
                            className="profile-img-file-input"
                            accept={IMAGE_ACCEPT}
                            onChange={onChangeSubjectHomeImage}
                            disabled={homeImageUploading}
                          />
                          <Label
                            htmlFor="subject_home_image"
                            className="profile-photo-edit avatar-xs"
                          >
                            <span className="avatar-title rounded-circle bg-light text-body">
                              {homeImageUploading && <Spinner />}
                              {!homeImageUploading && (
                                <i className="ri-camera-fill"></i>
                              )}
                            </span>
                          </Label>
                        </div>
                      )}
                  </div>
                </Col>
                <Col className="subject-detail-identity">
                  {hierarchyItems.length > 0 && (
                    <div className="subject-hierarchy">
                      {hierarchyItems.map((item, index) => (
                        <span className="subject-hierarchy__item" key={item.key}>
                          {index > 0 && (
                            <i
                              className={`subject-hierarchy__sep ${
                                i18n.language === "ar"
                                  ? "ri-arrow-left-s-line"
                                  : "ri-arrow-right-s-line"
                              } text-white-50`}
                            ></i>
                          )}
                          <Link
                            to={item.to}
                            className="subject-hierarchy__value text-white fw-medium text-decoration-none"
                            title={item.value}
                          >
                            {item.value}
                          </Link>
                        </span>
                      ))}
                    </div>
                  )}
                  <h3 className="subject-detail-title text-white mt-2 mb-1 fs-4 fs-lg-3">
                    {i18n.language === "ar"
                      ? subject?.name?.ar
                      : subject?.name.en}
                  </h3>
                  <h6 className="text-white mb-0">
                    {subject?.isActive ? t("active") : t("inActive")}
                  </h6>
                </Col>
              </Row>
            </div>
            <div className="subject-detail-toolbar">
              <div className="subject-detail-tabs">
                <SubjectTabMenu />
              </div>
              {profile &&
                profile.role &&
                profile?.role.key == RoleEnum.SUPER_ADMIN && (
                  <div className="subject-detail-actions">
                    {subject && (
                      <React.Fragment>
                        <Input
                          id="profile-foreground-img-file-input"
                          type="file"
                          className="profile-foreground-img-file-input d-none"
                          accept={IMAGE_ACCEPT}
                          onChange={onChangeSubjectContentImage}
                          disabled={contentImageUploading}
                        />
                        <Label
                          htmlFor="profile-foreground-img-file-input"
                          className="profile-photo-edit btn btn-light mb-0"
                        >
                          {!contentImageUploading && (
                            <i className="ri-image-edit-line align-bottom me-1"></i>
                          )}
                          {contentImageUploading && (
                            <Spinner
                              size={"sm"}
                              className="align-center"
                              style={{
                                marginBottom: -1,
                                marginRight: 3,
                                marginLeft: 3,
                              }}
                            />
                          )}
                          <span className="d-none d-lg-inline-block">
                            {t("uploadCover")}
                          </span>
                        </Label>
                        <Button
                          onClick={() => onEditSubject(subject.id)}
                          className="btn btn-success"
                        >
                          <i className="ri-edit-box-line align-bottom"></i>{" "}
                          <span className="d-none d-lg-inline-block">
                            {t("editItem", { item: "subject" })}
                          </span>
                        </Button>
                      </React.Fragment>
                    )}
                  </div>
                )}
            </div>
          </div>

          <Row>
            <Col lg={12}>
              <div className="subject-detail-content">
                <TabContent className="pt-4">
                  <TabPane>
                    <Card style={{ minHeight: "50vh" }}>
                      <CardBody>
                        <Row>
                          <Switch>
                            <Route exact={true} path={path}>
                              <Redirect to={`${url}/organizing${location.search}`} />
                            </Route>
                            <Route
                              path={`${path}/organizing`}
                              component={OrganizingLayoutPage}
                            />
                            <Route
                              path={`${path}/questions`}
                              component={QuestionsLayoutPage}
                            />
                            <Route
                              path={`${path}/notes`}
                              component={SubjectNotesPage}
                            />
                            <Route
                              path={`${path}/flash-cards-v2`}
                              component={FlashCardV2LayoutPage}
                            />
                            <Route
                              path={`${path}/lectures`}
                              component={LecturesLayoutPage}
                            />
                            <Route
                              path={`${path}/live-lectures`}
                              component={SubjectLiveLecturePage}
                            />
                            <Route
                              path={`${path}/documents`}
                              component={SubjectDocumentPage}
                            />
                            <Route
                              path={`${path}/instructions`}
                              component={SubjectInstructionPage}
                            />
                          </Switch>
                        </Row>
                      </CardBody>
                    </Card>
                  </TabPane>
                </TabContent>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      {editingSubjectId && (
        <AddEditSubjectModal
          gradeId={""}
          courseId={""}
          id={editingSubjectId}
          show={showAddEditSubjectModal}
          onCloseClick={() => {
            setShowAddEditSubjectModal(false);
            setEditingSubjectId(null);
          }}
        />
      )}
    </React.Fragment>
  );
};
export const SubjectDetailPage = withRouter(SubjectDetailPageComponent);
